import { useCallback, useEffect, useState } from "react";

import {
    getPosts,
    createPost,
    likePost,
    unlikePost,
    deletePost
} from "../api/postApi";

import {
    getComments,
    createComment,
    updateComment,
    deleteComment
} from "../api/commentApi";

export default function useCommunityFeed(category) {

    const [posts, setPosts] = useState([]);

    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);

    const [hasMore, setHasMore] = useState(true);

    const [error, setError] = useState(null);

    const [comments, setComments] = useState({});

    const [commentsLoading, setCommentsLoading] = useState({});

    /**
     * Load first page
     */
    const loadFeed = useCallback(async () => {

        setLoading(true);

        setError(null);

        try {

            const response = await getPosts(category, 0);

            setPosts(response.content || []);

            setHasMore(!response.last);

            setPage(1);

        } catch (e) {

            console.error(e);

            setError(e);

        } finally {

            setLoading(false);

        }

    }, [category]);

    /**
     * Load next page
     */
    const loadMore = useCallback(async () => {

        if (loading || !hasMore) return;

        setLoading(true);

        try {

            const response = await getPosts(category, page);

            setPosts(prev => [

                ...prev,

                ...(response.content || [])

            ]);

            setHasMore(!response.last);

            setPage(prev => prev + 1);

        } catch (e) {

            console.error(e);

        } finally {

            setLoading(false);

        }

    }, [category, page, loading, hasMore]);

    /**
     * Refresh feed
     */
    const refresh = useCallback(() => {

        loadFeed();

    }, [loadFeed]);

    /**
     * Create Post
     */
    const addPost = async (formData) => {

        const created = await createPost(formData);

        setPosts(prev => [

            created,

            ...prev

        ]);

        return created;

    };

    /**
     * Delete
     */
    const removePost = async (postId) => {

        await deletePost(postId);

        setPosts(prev =>

            prev.filter(

                p => p.id !== postId

            )

        );

    };

    const loadComments = useCallback(async (postId) => {

        if (!postId) {
            return;
        }

        setCommentsLoading(prev => ({
            ...prev,
            [postId]: true
        }));

        try {

            const response = await getComments(
                postId,
                0,
                20
            );

            setComments(prev => ({
                ...prev,
                [postId]: response.content || []
            }));

        } catch (e) {

            console.error(
                "Failed to load comments:",
                e
            );

            setComments(prev => ({
                ...prev,
                [postId]: []
            }));

        } finally {

            setCommentsLoading(prev => ({
                ...prev,
                [postId]: false
            }));

        }

    }, []);

    const addComment = async (
        postId,
        content
    ) => {

        if (!postId || !content?.trim()) {
            return;
        }

        try {

            const createdComment =
                await createComment(
                    postId,
                    content.trim()
                );

            setComments(prev => ({
                ...prev,

                [postId]: [
                    createdComment,
                    ...(prev[postId] || [])
                ]
            }));

            // Update post comment count immediately
            setPosts(prev =>
                prev.map(post =>
                    post.id === postId
                        ? {
                            ...post,
                            comments:
                                (post.comments || 0) + 1
                        }
                        : post
                )
            );

            return createdComment;

        } catch (e) {

            console.error(
                "Failed to create comment:",
                e
            );

            throw e;
        }
    };

    const editComment = async (
        postId,
        commentId,
        content
    ) => {

        if (!commentId || !content?.trim()) {
            return;
        }

        try {

            const updated =
                await updateComment(
                    commentId,
                    content.trim()
                );

            setComments(prev => ({
                ...prev,

                [postId]: (prev[postId] || []).map(
                    comment =>
                        comment.id === commentId
                            ? updated
                            : comment
                )
            }));

            return updated;

        } catch (e) {

            console.error(
                "Failed to update comment:",
                e
            );

            throw e;
        }
    };

    const removeComment = async (
        postId,
        commentId
    ) => {

        if (!commentId) {
            return;
        }

        try {

            await deleteComment(commentId);

            setComments(prev => ({
                ...prev,

                [postId]: (prev[postId] || []).filter(
                    comment =>
                        comment.id !== commentId
                )
            }));

            // Update post comment count
            setPosts(prev =>
                prev.map(post =>
                    post.id === postId
                        ? {
                            ...post,
                            comments: Math.max(
                                0,
                                (post.comments || 0) - 1
                            )
                        }
                        : post
                )
            );

        } catch (e) {

            console.error(
                "Failed to delete comment:",
                e
            );

            throw e;
        }
    };

    /**
     * Like
     *
     * Optimistic UI
     */
    const toggleLike = async (post) => {

        const liked = post.liked;

        setPosts(prev =>

            prev.map(item =>

                item.id === post.id

                    ? {

                        ...item,

                        liked: !liked,

                        likeCount: liked

                            ? item.likeCount - 1

                            : item.likeCount + 1

                    }

                    : item

            )

        );

        try {

            if (liked) {

                await unlikePost(post.id);

            }

            else {

                await likePost(post.id);

            }

        }

        catch (e) {

            refresh();

        }

    };

    useEffect(() => {

        loadFeed();

    }, [loadFeed]);

    return {

        // Posts
        posts,

        loading,
        hasMore,
        error,

        refresh,
        loadMore,

        addPost,
        removePost,

        toggleLike,

        // Comments
        comments,

        commentsLoading,

        loadComments,

        addComment,

        editComment,

        removeComment

    };

}